/**
 * KnitPaystubView — exact replica of Knit's employee paystub page:
 * company/employee header, Seq. Number, Pay Date, Pay Period, Payable
 * Earnings (Current + YTD), Tax Withholdings, Summary, Vacation Pay
 * Balance, with CSV export and Download PDF.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Download, FileSpreadsheet } from "lucide-react";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Section = ({ title, cols, rows, testid }) => (
  <table className="w-full text-left" data-testid={testid}>
    <thead>
      <tr className="border-b-2 border-[#2a7f8f]">
        <th className="py-1.5 pr-2 text-[13px] font-bold text-[#2a7f8f]">{title}</th>
        {cols.map((c) => (
          <th key={c[0]} className="py-1.5 text-right text-[13px] font-bold text-[#2a7f8f]">
            {c[0]}<br /><span className="text-[11px] font-normal">{c[1]}</span>
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} className={r.bold ? "border-t-2 border-amber-500" : ""} data-testid={r.testid}>
          <td className={`py-1.5 pr-2 text-[13px] ${r.bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{r.label}</td>
          {r.cells.map((c, j) => (
            <td key={j} className={`py-1.5 text-right font-mono text-[12px] ${r.bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{c}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export default function KnitPaystubView({ paystubId, onClose }) {
  const [view, setView] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/payroll/paystubs/${paystubId}/view`);
        setView(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load paystub");
        onClose();
      }
    })();
  }, [paystubId, onClose]);

  if (!view) {
    return (
      <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60" data-testid="knit-paystub-loading">
        <p className="rounded-lg bg-white px-4 py-3 font-mono text-xs uppercase tracking-widest text-slate-500">Loading paystub…</p>
      </div>
    );
  }

  const { stub, employee: emp, ytd, vacation, company: co } = view;
  const gross = Number(stub.gross_pay_total ?? stub.gross_pay ?? 0);
  const rate = Number(stub.hourly_rate || 0);
  const weekEnd = (() => {
    try {
      const d = new Date(`${stub.week_start}T12:00:00`);
      d.setDate(d.getDate() + 6);
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  })();
  const payDate = (stub.created_at || "").slice(0, 10);
  const seq = String(stub.seq_number || 0).padStart(9, "0");
  const curCpp = Number(stub.cpp_employee || 0) + Number(stub.cpp2_employee || 0);
  const ded = Number(stub.total_deductions || 0);
  const reimb = Number(stub.reimbursement_total || 0);
  const holidayTotal = Number(stub.holiday_pay || 0) + Number(stub.stat_holiday_pay || 0);

  const addr = (o, withName) => [
    withName ? (o.name || stub.employee_name || "") : (o.legal_name || ""),
    [o.address_street, o.address_line2].filter(Boolean).join(" ") || o.address_line1,
    [o.address_city || o.city, o.address_province || o.province].filter(Boolean).join(", ")
      + ((o.address_postal || o.postal) ? `, ${o.address_postal || o.postal}` : ""),
  ].filter(Boolean);

  const earningRows = [];
  if (stub.pay_type === "salary") {
    earningRows.push({ label: "Regular Pay", cells: ["", "", "", money(stub.regular_pay), "", ""] });
  } else {
    // Personal Vehicle allowance (+2h/day) is carved out of the Regular line
    // so it reads as its own line — totals are unchanged.
    const vehH = Number(stub.vehicle_hours || 0);
    const vehPay = Number(stub.vehicle_pay || 0);
    earningRows.push({
      label: "Regular Pay",
      cells: ["", money(rate), (Number(stub.regular_hours || 0) - vehH).toFixed(2), money(Number(stub.regular_pay || 0) - vehPay),
              Number(ytd.hours || 0).toFixed(2), money(ytd.earnings)],
    });
    if (vehH) {
      earningRows.push({ label: "Personal Vehicle", testid: "paystub-vehicle-line", cells: ["", money(rate), vehH.toFixed(2), money(vehPay), "", ""] });
    }
    if (Number(stub.overtime_hours || 0)) {
      earningRows.push({ label: "Overtime", cells: ["", money(rate * 1.5), Number(stub.overtime_hours).toFixed(2), money(stub.overtime_pay), "", ""] });
    }
  }
  (stub.recurring_income_lines || []).filter((l) => l.taxed).forEach((l) => {
    earningRows.push({
      label: l.type,
      cells: ["", l.unit === "hours" ? money(rate) : "", l.unit === "hours" ? Number(l.amount).toFixed(2) : "", money(l.dollars), "", ""],
    });
  });
  if (holidayTotal) earningRows.push({ label: "Holiday Pay", cells: ["", "", "", money(holidayTotal), "", ""] });
  if (Number(stub.vacation_paid_out || 0)) earningRows.push({ label: "Vacation Pay", cells: ["", "", "", money(stub.vacation_paid_out), "", ""] });
  if (Number(stub.cra_repayment || 0)) earningRows.push({ label: "CRA Repayment", cells: ["", "", "", money(stub.cra_repayment), "", ""] });
  earningRows.push({ label: "Total Payable Earnings", bold: true, cells: ["", "", "", money(gross), "", money(ytd.earnings)] });

  const downloadCsv = () => {
    const lines = [
      ["PAYSTUB"],
      [co.legal_name || ""], addr({ ...co, address_street: co.address_line1, address_line2: co.address_line2, address_city: co.city, address_province: co.province, address_postal: co.postal }, false)[1] || "",
      [`Employee: ${emp.name || stub.employee_name}`],
      [`Seq. Number: ${seq}`, `Pay Date: ${payDate}`, `Pay Period: ${stub.week_start} to ${weekEnd}`],
      [],
      ["Payable Earnings", "Rate", "Current Hours/Unit", "Current Earnings", "YTD Hours/Unit", "YTD Earnings"],
      ...earningRows.map((r) => [r.label, ...r.cells]),
      [],
      ["Tax Withholdings", "Current Amount", "YTD Amount"],
      ["Employee EI", money(stub.ei_employee), money(ytd.ei)],
      ["Employee CPP", money(curCpp), money(ytd.cpp)],
      ["Federal Tax", money(stub.federal_tax), money(ytd.federal_tax)],
      ["ON Tax", money(stub.provincial_tax), money(ytd.provincial_tax)],
      ["Total Deductions", money(ded), money(ytd.total_deductions)],
      [],
      ["Summary", "Current Amount", "YTD Amount"],
      ["Total Gross", money(gross), money(ytd.earnings)],
      ["Reimbursement", money(reimb), money(ytd.reimbursement)],
      ["Total Non-Payable Earnings", money(reimb), money(ytd.reimbursement)],
      ["Total Deductions", `-${money(ded)}`, `-${money(ytd.total_deductions)}`],
      ["Pay Amount", money(stub.net_pay), money(ytd.net)],
      [],
      ["Vacation Pay Balance(Earned)", "Used", "Balance"],
      [money(vacation.earned), money(vacation.used), money(vacation.balance)],
    ];
    const csv = lines.map((r) => (r || []).map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "employee_paystub.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/payroll/paystubs/${paystubId}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "employee_paystub.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Could not download the PDF");
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid="knit-paystub-view">
      <div className="relative my-6 w-full max-w-4xl rounded-lg bg-white p-8 text-slate-800 shadow-2xl">
        <div className="mb-6 flex items-center justify-between border-b border-slate-200 pb-4">
          <h2 className="text-xl font-bold tracking-wide text-slate-900">PAYSTUB</h2>
          <div className="flex items-center gap-3">
            <button onClick={downloadCsv} data-testid="paystub-csv-btn"
              className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600 hover:text-amber-700">
              <FileSpreadsheet className="h-4 w-4" /> CSV
            </button>
            <button onClick={downloadPdf} data-testid="paystub-pdf-btn"
              className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600 hover:text-amber-700">
              <Download className="h-4 w-4" /> Download PDF
            </button>
            <button onClick={onClose} aria-label="Close" data-testid="paystub-view-close"
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* header: company | employee | seq/pay date */}
        <div className="grid grid-cols-3 gap-6 text-[13px] leading-5">
          <div>{addr(co, false).map((l, i) => <p key={i}>{l}</p>)}</div>
          <div>
            {addr(emp, true).map((l, i) => <p key={i}>{l}</p>)}
            <p className="mt-3 font-bold">Pay Period</p>
            <p>{stub.week_start} to {weekEnd}</p>
          </div>
          <div>
            <p><span className="font-bold">Seq. Number</span>&nbsp;&nbsp;{seq}</p>
            <p><span className="font-bold">Pay Date</span>&nbsp;&nbsp;{payDate}</p>
          </div>
        </div>

        <div className="mt-8 space-y-8">
          <Section title="Payable Earnings" testid="paystub-earnings"
            cols={[["Code", "Job Code"], ["Rate", "Hourly Rate"], ["Current", "Hours/Unit"], ["Current", "Earnings"], ["YTD", "Hours/Unit"], ["YTD", "Earnings"]]}
            rows={earningRows} />
          <Section title="Tax Withholdings" testid="paystub-taxes"
            cols={[["Current", "Amount"], ["YTD", "Amount"]]}
            rows={[
              { label: "Employee EI", cells: [money(stub.ei_employee), money(ytd.ei)] },
              { label: "Employee CPP", cells: [money(curCpp), money(ytd.cpp)] },
              { label: "Federal Tax", cells: [money(stub.federal_tax), money(ytd.federal_tax)] },
              { label: "ON Tax", cells: [money(stub.provincial_tax), money(ytd.provincial_tax)] },
              { label: "Total Deductions", bold: true, cells: [money(ded), money(ytd.total_deductions)] },
            ]} />
          <Section title="Summary" testid="paystub-summary"
            cols={[["Current", "Amount"], ["YTD", "Amount"]]}
            rows={[
              { label: "Total Gross", cells: [money(gross), money(ytd.earnings)] },
              { label: "Reimbursement", cells: [money(reimb), money(ytd.reimbursement)] },
              { label: "Total Non-Payable Earnings", cells: [money(reimb), money(ytd.reimbursement)] },
              { label: "Total Deductions", cells: [`-${money(ded)}`, `-${money(ytd.total_deductions)}`] },
              { label: "Pay Amount", bold: true, cells: [money(stub.net_pay), money(ytd.net)] },
            ]} />
          <Section title="Vacation Pay Balance(Earned)" testid="paystub-vacation"
            cols={[["Used", ""], ["Balance", ""]]}
            rows={[{ label: money(vacation.earned), cells: [money(vacation.used), money(vacation.balance)] }]} />
        </div>
      </div>
    </div>
  );
}
